/* eslint-disable @next/next/no-img-element */
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { AffiliateDashboardData } from "../../api";
import { useInput, UseInputValidator } from "../../hooks/useInput";
import Button, { ButtonProps } from "../general/buttonv2";
import { Collapse } from "../general/collapse";
import { Input } from "../general/input";

const StyledAffiliatePanel = styled.div`
  h2 {
    font-size: 22px;
  }
`;

const CatMarketContainer = styled.div`
  width: 50%;

  @media screen and (max-width: 800px) {
    display: none;
  }
`;

const EnrollInAffiliate = styled.div`
  width: 50%;
  padding: 0 40px 0 0;
  @media screen and (max-width: 800px) {
    width: 100%;
  }
`;

const CardDiv = styled.div<{ size?: "third" | "half"; spacing?: number }>`
  min-width: ${({ size, spacing }) =>
    `calc(100% * ${size ? (size === "third" ? "0.3333" : ".5") : "1"} - ${spacing ?? 0}px)`};
  border-radius: 6px;
  padding: 15px;
  background-color: var(--raised-background-color-three);
  display: flex;
  flex-flow: column;
  justify-content: center;

  @media screen and (max-width: 700px) {
    min-width: 100%;
    margin: 10px 0 0 0;
  }

  img {
    width: 26px;
  }
  h2 {
    vertical-align: middle;
    font-size: 18px;
    margin: 0;
  }
  h3 {
    font-size: 18px;
    margin: 10px 0 0 0;
  }
`;

const Flex = styled.div<{
  $flow?: "row" | "column";
  $alignItems?: React.CSSProperties["alignItems"];
  $justifyContent?: React.CSSProperties["justifyContent"];
}>`
  flex-flow: ${({ $flow = "row" }) => $flow} wrap;
  align-items: ${({ $alignItems = "unset" }) => $alignItems};
  justify-content: ${({ $justifyContent = "unset" }) => $justifyContent};
  display: flex;
`;

const InputContainerDiv = styled.div`
  display: flex;
  height: 55px;
  width: 100%;
  @media screen and (max-width: 700px) {
    width: 100%;
    margin: 10px 0 0 0;
  }
`;

const InputWrapperDiv = styled(Flex)`
  width: 70%;
  align-items: flex-end;
  > p {
    margin: 4px 0 0 0;
    color: #e74c3c;
    font-size: 12px;
  }
  @media screen and (max-width: 700px) {
    width: 100%;
  }
`;

const Stat = styled.p`
  font-size: 32px;
  font-weight: 400;
  margin: 0;
`;

const CardWrapperDiv = styled(Flex)`
  margin: 15px 0 0 0;
  justify-content: space-between;
  @media screen and (max-width: 700px) {
    margin: 0;
  }
`;

const UpdateInputContainer = styled(CardDiv)`
  flex-flow: row;
  margin: 15px 0 0 0;
  @media screen and (max-width: 700px) {
    flex-flow: column;
  }
`;

const TitleContainerDiv = styled(Flex)`
  flex-flow: column;
  justify-content: start;
  width: 50%;
  padding: 0 30px 0 0;
  @media screen and (max-width: 700px) {
    width: 100%;
  }
  > p {
    margin: 0;
    font-size: 12px;
    > span {
      font-weight: 600;
    }
  }
`;

interface AffiliateInputProps extends Omit<React.HTMLProps<HTMLInputElement>, "onSubmit"> {
  apiError?: string;
  onSubmit?: (value: string) => void;
  disabled?: boolean;
  value?: string;
  buttonText: string;
  validate?: UseInputValidator;
}

const AffiliateButton = (props: ButtonProps) => (
  <Button
    {...props}
    wrapperStyle={{
      height: "calc(100% - 4px)",
      minWidth: "30%",
      marginLeft: "15px",
    }}
    style={{
      height: "100%",
      width: "100%",
      display: "block",
      textAlign: "center",
    }}
  />
);

const AffiliateInput = ({
  apiError,
  onSubmit,
  disabled,
  validate,
  value: defaultValue,
  buttonText,
  ...rest
}: AffiliateInputProps) => {
  const { props, value, error } = useInput({
    value: defaultValue ?? "",
    error: apiError,
    validate,
  });
  const [touched, setTouched] = useState(false);

  const submit = useCallback(() => {
    if (!value || value === "" || error || !touched) {
      return;
    }

    onSubmit?.(value);
  }, [onSubmit, error, value, touched]);

  useEffect(() => {
    const initialValue = defaultValue ?? "";
    if (value === initialValue) {
      setTouched(false);
      return;
    }
    setTouched(true);
  }, [value, defaultValue]);

  const buttonDisabled = disabled || !!error || !touched;

  return (
    <InputWrapperDiv>
      <InputContainerDiv>
        <Input {...rest} {...props} />
        <AffiliateButton disabled={buttonDisabled} onClick={() => submit()}>
          {buttonText}
        </AffiliateButton>
      </InputContainerDiv>
      {error ? <p>{error}</p> : null}
    </InputWrapperDiv>
  );
};

interface ReadOnlyAffiliateInputProps extends Omit<React.HTMLProps<HTMLInputElement>, "onSubmit"> {
  apiError?: string;
  onSubmit?: () => void;
  disabled?: boolean;
  value?: string;
  buttonText: string;
  validate?: UseInputValidator;
}

const ReadOnlyAffiliateInput = ({
  onSubmit,
  disabled,
  validate,
  value: defaultValue,
  buttonText,
  ...rest
}: ReadOnlyAffiliateInputProps) => {
  return (
    <InputWrapperDiv>
      <InputContainerDiv>
        <Input wrapperStyle={{ width: "100%" }} readOnly {...rest} value={defaultValue} />
        <AffiliateButton onClick={() => onSubmit?.()}>{buttonText}</AffiliateButton>
      </InputContainerDiv>
    </InputWrapperDiv>
  );
};

interface AffiliateUnconfiguredProps {
  createDefaultCode: () => Promise<void>;
}

export const AffiliateUnconfigured = ({ createDefaultCode }: AffiliateUnconfiguredProps) => {
  return (
    <StyledAffiliatePanel>
      <Flex>
        <EnrollInAffiliate>
          <h1>Redact Affiliate Program: Become a cool kitty cat</h1>
          <p>Redact offers an affiliate program. Advertise Redact and earn some cash!</p>
          <Button onClick={createDefaultCode}>Enroll</Button>
        </EnrollInAffiliate>
        <CatMarketContainer>
          <img alt="cat market" src="/images/affiliate/cat_market.svg" />
        </CatMarketContainer>
      </Flex>
    </StyledAffiliatePanel>
  );
};

const AffiliateCollapsibleDiv = styled.div`
  h1 {
    margin: 0;
    font-size: 13px;
    font-weight: 4 00;
    span {
      color: #7551cb;
      font-size: 14px;
      margin: 0 5px 0 0;
      font-weight: 600;
    }
  }
`;

interface AffiliateProps {
  dashboardData: AffiliateDashboardData;
  loading: boolean;
  updatePaypalEmail: (email: string) => Promise<void>;
  createCode: (code: string) => Promise<void>;
  createCustomCodeError?: string;
}

const emailRegex =
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

export const AffiliateConfigured = ({
  dashboardData,
  createCustomCodeError,
  loading,
  updatePaypalEmail,
  createCode,
}: AffiliateProps) => {
  const copyToClipboard = (val: string) => navigator.clipboard.writeText(val);
  const createCodeValidator: UseInputValidator = useCallback((e) => {
    const valid = /^[A-Z0-9]*$/.test(e);
    if (!valid) {
      return {
        valid,
        message: "Code can only contain uppercase alphanumeric values",
      };
    }

    return { valid };
  }, []);

  const emailValidator: UseInputValidator = useCallback((e) => {
    const valid = emailRegex.test(e);
    if (!valid) {
      return { valid, message: "You must enter a valid email address" };
    }

    return { valid };
  }, []);

  const doesntHaveCustomCode = !dashboardData.custom;

  return (
    <StyledAffiliatePanel>
      <Collapse title={"How does the affiliate program work?"}>
        <AffiliateCollapsibleDiv>
          <h1 className="text-lg">
            <span>1</span>Choose a code
          </h1>
          <p className="m-0 mb-[6px]">
            Now that you have enrolled into the affiliate program, you have been assigned a default
            code. If you prefer a code that represents you or your brand better, you can create a
            custom one.
          </p>
          <h1>
            <span>2</span>Copy your link
          </h1>
          <p className="m-0 mb-[6px]">
            Click on the copy link button next to the code that you{"'"}ve decided to use. This will
            copy the formatted link to your clipboard
          </p>
          <h1>
            <span>3</span>Promote redact
          </h1>
          <p className="m-0 mb-[20px]">
            Promote your link on your social media, or send it to your friends! When someone clicks
            the link, logs in, and then buys redact, you will be given credit for the purchase. The
            purchase must be made within 14 days of clicking the link. Clicking the link again will
            reset the 14 days.
          </p>
          <p className="m-0">
            We currently only make affiliate payout payments to paypal accounts. To configure the
            account you want the money to be sent to, enter it in the input below where it says
            Paypal Email.
          </p>
        </AffiliateCollapsibleDiv>
      </Collapse>
      <UpdateInputContainer>
        <TitleContainerDiv>
          <h2>Paypal email</h2>
          <p>
            The paypal email you{`'`}d like earnings you make on your affiliated members to be paid
            out to.
          </p>
        </TitleContainerDiv>
        <AffiliateInput
          buttonText="Update"
          name="Paypal email"
          placeholder="Enter your paypal email"
          disabled={loading}
          value={dashboardData.paypalEmail}
          onSubmit={(val) => updatePaypalEmail(val)}
          validate={emailValidator}
        />
      </UpdateInputContainer>
      <UpdateInputContainer>
        <TitleContainerDiv>
          <h2>Default affiliate Code</h2>
          <p>Your default, automatically designated affiliate code.</p>
        </TitleContainerDiv>
        <ReadOnlyAffiliateInput
          name="Default affiliate code"
          value={dashboardData.default}
          buttonText="Copy Link"
          onSubmit={() =>
            copyToClipboard(`https://redact.dev?affiliateCode=${dashboardData.default}`)
          }
        />
      </UpdateInputContainer>
      <UpdateInputContainer>
        <TitleContainerDiv>
          <h2>Custom affiliate code</h2>
          <p>
            {doesntHaveCustomCode ? (
              <>
                Set a custom code that represents you better.{" "}
                <span>Once you{`'`}ve set a custom code, you cannot change it!</span>
              </>
            ) : (
              "Your custom affiliate code"
            )}
          </p>
        </TitleContainerDiv>
        {doesntHaveCustomCode ? (
          <AffiliateInput
            buttonText="Set"
            name="Custom affiliate code"
            placeholder="Enter a custom code"
            apiError={createCustomCodeError}
            disabled={loading}
            onSubmit={(val) => createCode(val)}
            validate={createCodeValidator}
          />
        ) : (
          <ReadOnlyAffiliateInput
            name="Custom affiliate code"
            value={dashboardData.custom}
            buttonText="Copy Link"
            onSubmit={() =>
              copyToClipboard(`https://redact.dev?affiliateCode=${dashboardData.custom}`)
            }
          />
        )}
      </UpdateInputContainer>
      <CardWrapperDiv>
        <CardDiv spacing={10} size="third">
          <img alt="total sales" src="/images/affiliate/total_sales.svg" />
          <h3>Total sales</h3>
          <Stat>{dashboardData.stats.totalSales}</Stat>
        </CardDiv>
        <CardDiv spacing={10} size="third">
          <img alt="unpaid earnings" src="/images/affiliate/unpaid_earnings.svg" />
          <h3>Unpaid earnings</h3>
          <Stat>${dashboardData.stats.unpaidEarnings}</Stat>
        </CardDiv>
        <CardDiv spacing={10} size="third">
          <img alt="total paid to date" src="/images/affiliate/total_paid.svg" />
          <h3>Total paid to date</h3>
          <Stat>${dashboardData.stats.totalPaidToDate}</Stat>
        </CardDiv>
      </CardWrapperDiv>
      <CardWrapperDiv>
        <CardDiv spacing={7} size="half">
          <img alt="all time traffic" src="/images/affiliate/traffic.svg" />
          <h3>All time traffic</h3>
          <Stat>{dashboardData.stats.allTimeTraffic}</Stat>
        </CardDiv>
        <CardDiv spacing={7} size="half">
          <img alt="traffic since last payment" src="/images/affiliate/traffic_payment.svg" />
          <h3>Traffic since last payment</h3>
          <Stat>{dashboardData.stats.trafficSinceLastPayment}</Stat>
        </CardDiv>
      </CardWrapperDiv>
    </StyledAffiliatePanel>
  );
};
